/** A piece of linkified text: plain text, or an http(s) link whose `text` is also its `href`. */
export type TextPart =
    | { type: 'text'; text: string }
    | { type: 'link'; text: string; href: string };

const URL_PATTERN = /https?:\/\/[^\s<>]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?'"]$/;
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

/** Count of `char` in `text`. */
function count(text: string, char: string): number {
    return text.split(char).length - 1;
}

/** Drop sentence punctuation, and a closing bracket nothing in the URL opened, from the end of a matched URL. */
function trimUrl(url: string): string {
    let end = url;
    for (;;) {
        const last = end.at(-1) ?? '';
        const opener = CLOSERS[last];
        const unbalanced = opener && count(end, last) > count(end, opener);
        if (TRAILING_PUNCTUATION.test(end) || unbalanced) {
            end = end.slice(0, -1);
        } else {
            return end;
        }
    }
}

function isSafeUrl(url: string): boolean {
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Split text into plain and link parts. Only http and https URLs become links;
 * any other scheme (`javascript:`, `data:`) and text without a URL stay plain.
 * No markdown and no HTML: the description is shown as text.
 */
export function linkify(text: string): TextPart[] {
    const parts: TextPart[] = [];
    let cursor = 0;
    for (const match of text.matchAll(URL_PATTERN)) {
        const url = trimUrl(match[0]);
        const start = match.index;
        if (!isSafeUrl(url)) continue;
        if (start > cursor) {
            parts.push({ type: 'text', text: text.slice(cursor, start) });
        }
        parts.push({ type: 'link', text: url, href: url });
        cursor = start + url.length;
    }
    if (cursor < text.length) {
        parts.push({ type: 'text', text: text.slice(cursor) });
    }
    return parts;
}
